import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import IntegrationApi from './IntegrationApi';
import ChatPanel from './ChatPanel';
import {
  MessageSquare, Send, Phone, Search, Filter, RefreshCw,
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Settings,
  ChevronLeft, ChevronRight, Clock, CheckCircle2, XCircle,
  AlertCircle, Eye, Smartphone, Mail, Inbox, Bell, CheckCheck
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  sent: { label: 'Sent', class: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Delivered', class: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Failed', class: 'bg-red-100 text-red-700' },
  read: { label: 'Read', class: 'bg-purple-100 text-purple-700' },
  received: { label: 'Received', class: 'bg-amber-100 text-amber-700' },
  queued: { label: 'Queued', class: 'bg-slate-100 text-slate-700' },
};

const CHANNEL_BADGES = {
  sms: { label: 'SMS', class: 'bg-cyan-100 text-cyan-700' },
  whatsapp: { label: 'WhatsApp', class: 'bg-green-100 text-green-700' },
};

const EVENT_LABELS = {
  application_received: 'Application Received',
  loan_approved: 'Loan Approved',
  loan_declined: 'Loan Declined',
  payment_reminder: 'Payment Reminder',
  payment_overdue: 'Payment Overdue',
  manual: 'Manual',
  reply: 'Reply',
};

export default function Communications({ getAuthHeader, onUnreadChange }) {
  const [subTab, setSubTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [msgPage, setMsgPage] = useState(0);
  const [msgSearch, setMsgSearch] = useState('');
  const [msgChannelFilter, setMsgChannelFilter] = useState('all');
  const [msgStatusFilter, setMsgStatusFilter] = useState('all');
  const [msgDirectionFilter, setMsgDirectionFilter] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [variables, setVariables] = useState([]);
  const [sendDialog, setSendDialog] = useState({ open: false });
  const [sendForm, setSendForm] = useState({ customer_id: '', application_id: '', channel: 'sms', content: '' });
  const [templateDialog, setTemplateDialog] = useState({ open: false, template: null });
  const [templateForm, setTemplateForm] = useState({ event: '', name: '', sms_template: '', whatsapp_template: '', channels: ['sms', 'whatsapp'], is_active: true });
  const [previewDialog, setPreviewDialog] = useState({ open: false, message: null });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState([]);
  const [repliesTotal, setRepliesTotal] = useState(0);
  const [repliesPage, setRepliesPage] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    const pollChat = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/chat/unread-count`, getAuthHeader());
        setChatUnread(res.data.count || 0);
      } catch { /* ignore */ }
    };
    pollChat();
    const interval = setInterval(pollChat, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const PAGE_SIZE = 50;

  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: msgPage * PAGE_SIZE });
      if (msgSearch) params.set('search', msgSearch);
      if (msgChannelFilter !== 'all') params.set('channel', msgChannelFilter);
      if (msgStatusFilter !== 'all') params.set('status', msgStatusFilter);
      if (msgDirectionFilter !== 'all') params.set('direction', msgDirectionFilter);
      const res = await axios.get(`${API_URL}/admin/messaging/messages?${params}`, getAuthHeader());
      setMessages(res.data.messages);
      setMsgTotal(res.data.total);
    } catch (e) { toast.error('Failed to load messages'); }
  }, [msgPage, msgSearch, msgChannelFilter, msgStatusFilter, msgDirectionFilter, getAuthHeader]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/messaging/templates`, getAuthHeader());
      setTemplates(res.data);
    } catch (e) { toast.error('Failed to load templates'); }
  }, [getAuthHeader]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/messaging/config`, getAuthHeader());
      setConfig(res.data);
    } catch (e) { toast.error('Failed to load config'); }
  }, [getAuthHeader]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/messaging/stats`, getAuthHeader());
      setStats(res.data);
    } catch (e) { toast.error('Failed to load stats'); }
  }, [getAuthHeader]);

  const fetchVariables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/messaging/variables`, getAuthHeader());
      setVariables(res.data);
    } catch (e) { /* ignore */ }
  }, [getAuthHeader]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users`, getAuthHeader());
      setCustomers((res.data || []).filter(u => u.role === 'client'));
    } catch (e) { /* ignore */ }
  }, [getAuthHeader]);

  const fetchReplies = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: repliesPage * PAGE_SIZE, direction: 'inbound' });
      const res = await axios.get(`${API_URL}/admin/messaging/messages?${params}`, getAuthHeader());
      setReplies(res.data.messages);
      setRepliesTotal(res.data.total);
    } catch (e) { /* ignore */ }
  }, [repliesPage, getAuthHeader]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/messaging/unread-count`, getAuthHeader());
      const count = res.data.unread || 0;
      setUnreadCount(count);
      if (onUnreadChange) onUnreadChange(count);
    } catch (e) { /* ignore */ }
  }, [getAuthHeader, onUnreadChange]);

  useEffect(() => {
    fetchConfig();
    fetchStats();
    fetchVariables();
    fetchCustomers();
    fetchUnread();
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchConfig, fetchStats, fetchVariables, fetchCustomers, fetchUnread]);

  useEffect(() => {
    if (subTab === 'messages') fetchMessages();
    if (subTab === 'templates') fetchTemplates();
    if (subTab === 'replies') { fetchReplies(); fetchUnread(); }
  }, [subTab, fetchMessages, fetchTemplates, fetchReplies, fetchUnread]);

  const handleSendMessage = async () => {
    if (!sendForm.customer_id || !sendForm.content.trim()) {
      toast.error('Select a customer and enter message content');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/messaging/send`, sendForm, getAuthHeader());
      toast.success('Message sent (mock provider)');
      setSendDialog({ open: false });
      setSendForm({ customer_id: '', application_id: '', channel: 'sms', content: '' });
      fetchMessages();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setLoading(false); }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.event.trim()) {
      toast.error('Event and name are required');
      return;
    }
    setLoading(true);
    try {
      if (templateDialog.template) {
        await axios.put(`${API_URL}/admin/messaging/templates/${templateDialog.template.id}`, templateForm, getAuthHeader());
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/admin/messaging/templates`, templateForm, getAuthHeader());
        toast.success('Template created');
      }
      setTemplateDialog({ open: false, template: null });
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setLoading(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/admin/messaging/templates/${id}`, getAuthHeader());
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleToggleTemplate = async (tmpl) => {
    try {
      await axios.put(`${API_URL}/admin/messaging/templates/${tmpl.id}`, { is_active: !tmpl.is_active }, getAuthHeader());
      fetchTemplates();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const handleUpdateConfig = async (updates) => {
    try {
      await axios.put(`${API_URL}/admin/messaging/config`, updates, getAuthHeader());
      toast.success('Config saved');
      fetchConfig();
    } catch (e) { toast.error('Config save failed'); }
  };

  const handleToggleRule = async (event, field, value) => {
    if (!config) return;
    const rules = { ...config.automation_rules };
    rules[event] = { ...rules[event], [field]: value };
    await handleUpdateConfig({ automation_rules: rules });
  };

  const handleMarkRead = async (msgId) => {
    try {
      await axios.put(`${API_URL}/admin/messaging/messages/${msgId}/read`, {}, getAuthHeader());
      setReplies(prev => prev.map(m => m.id === msgId ? { ...m, is_read: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
      if (onUnreadChange) onUnreadChange(Math.max(0, unreadCount - 1));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/admin/messaging/messages/mark-all-read`, {}, getAuthHeader());
      toast.success('All replies marked as read');
      setReplies(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnreadCount(0);
      if (onUnreadChange) onUnreadChange(0);
    } catch (e) { toast.error('Failed to mark all read'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const totalPages = Math.ceil(msgTotal / PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="communications-tab">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={unreadCount > 0 ? 'ring-2 ring-amber-400 ring-offset-1' : ''}>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${unreadCount > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                <Inbox className={`w-5 h-5 ${unreadCount > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Unread Replies</p>
                <p className={`text-xl font-bold tabular-nums ${unreadCount > 0 ? 'text-amber-700' : ''}`} data-testid="stats-unread-replies">{unreadCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-50"><MessageSquare className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Total Messages</p>
                <p className="text-xl font-bold tabular-nums" data-testid="stats-total-messages">{stats?.total_all_time ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-50"><Send className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500">This Month</p>
                <p className="text-xl font-bold tabular-nums" data-testid="stats-monthly-messages">{stats?.total_this_month ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-cyan-50"><Phone className="w-5 h-5 text-cyan-600" /></div>
              <div>
                <p className="text-xs text-slate-500">SMS This Month</p>
                <p className="text-xl font-bold tabular-nums">{stats?.monthly?.sms?.total ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-50"><Smartphone className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500">WhatsApp This Month</p>
                <p className="text-xl font-bold tabular-nums">{stats?.monthly?.whatsapp?.total ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tabs */}
      <Tabs value={subTab} onValueChange={setSubTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="chat" data-testid="msg-chat-tab" className="relative">
              <MessageSquare className="w-4 h-4 mr-1" />Live Chat
              {chatUnread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-1 animate-pulse" data-testid="chat-badge">{chatUnread}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="replies" data-testid="msg-replies-tab" className="relative">
              <Inbox className="w-4 h-4 mr-1" />Replies
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-1 animate-pulse" data-testid="replies-badge">{unreadCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="msg-log-tab"><MessageSquare className="w-4 h-4 mr-1" />Message Log</TabsTrigger>
            <TabsTrigger value="templates" data-testid="msg-templates-tab"><Mail className="w-4 h-4 mr-1" />Templates</TabsTrigger>
            <TabsTrigger value="automation" data-testid="msg-automation-tab"><Settings className="w-4 h-4 mr-1" />Automation</TabsTrigger>
            <TabsTrigger value="config" data-testid="msg-config-tab"><Settings className="w-4 h-4 mr-1" />Provider</TabsTrigger>
            <TabsTrigger value="integration" data-testid="msg-integration-tab"><Smartphone className="w-4 h-4 mr-1" />API & Webhook</TabsTrigger>
          </TabsList>
          <Button size="sm" onClick={() => { setSendDialog({ open: true }); setSendForm({ customer_id: '', application_id: '', channel: 'sms', content: '' }); }} data-testid="send-message-btn">
            <Send className="w-4 h-4 mr-1" />Send Message
          </Button>
        </div>

        {/* ======== INTEGRATION API ======== */}
        <TabsContent value="integration">
          <IntegrationApi getAuthHeader={getAuthHeader} />
        </TabsContent>

        {/* ======== LIVE CHAT ======== */}
        <TabsContent value="chat">
          <ChatPanel getAuthHeader={getAuthHeader} />
        </TabsContent>

        {/* ======== REPLIES INBOX ======== */}
        <TabsContent value="replies" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    Reply Inbox
                    {unreadCount > 0 && <Badge className="bg-amber-100 text-amber-700">{unreadCount} unread</Badge>}
                  </CardTitle>
                  <CardDescription>Inbound customer replies — newest first</CardDescription>
                </div>
                <div className="flex gap-2">
                  {unreadCount > 0 && (
                    <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                      <CheckCheck className="w-3.5 h-3.5 mr-1" />Mark All Read
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { fetchReplies(); fetchUnread(); }}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {replies.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No inbound replies yet</p>
                  <p className="text-xs mt-1">Customer replies will appear here when they respond to messages</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {replies.map(msg => {
                      const isUnread = !msg.is_read;
                      const cBadge = CHANNEL_BADGES[msg.channel] || { label: msg.channel, class: 'bg-slate-100 text-slate-700' };
                      return (
                        <div
                          key={msg.id}
                          className={`border rounded-lg p-4 transition-all cursor-pointer hover:shadow-sm ${isUnread ? 'bg-amber-50 border-amber-200' : 'bg-white hover:bg-slate-50'}`}
                          onClick={() => {
                            if (isUnread) handleMarkRead(msg.id);
                            setPreviewDialog({ open: true, message: msg });
                          }}
                          data-testid={`reply-${msg.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {isUnread && <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0 animate-pulse" data-testid={`unread-dot-${msg.id}`} />}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm">{msg.customer_name || 'Unknown'}</span>
                                  <Badge className={`text-[10px] ${cBadge.class}`}>{cBadge.label}</Badge>
                                  <span className="text-xs text-slate-400">{msg.from_number}</span>
                                </div>
                                <p className={`text-sm ${isUnread ? 'font-medium text-slate-800' : 'text-slate-600'} line-clamp-2`}>{msg.content}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(msg.created_at)}</span>
                              {isUnread && <Badge className="bg-amber-100 text-amber-700 text-[10px]">New</Badge>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Math.ceil(repliesTotal / PAGE_SIZE) > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-xs text-slate-500">{repliesTotal} replies total</p>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" disabled={repliesPage === 0} onClick={() => setRepliesPage(p => p - 1)}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="px-3 py-1.5 text-sm">{repliesPage + 1} / {Math.ceil(repliesTotal / PAGE_SIZE)}</span>
                        <Button variant="outline" size="sm" disabled={repliesPage >= Math.ceil(repliesTotal / PAGE_SIZE) - 1} onClick={() => setRepliesPage(p => p + 1)}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======== MESSAGES LOG ======== */}
        <TabsContent value="messages" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-base">Message Log</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchMessages}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search phone, name, ref..." value={msgSearch} onChange={e => { setMsgSearch(e.target.value); setMsgPage(0); }} className="pl-9" data-testid="msg-search" />
                </div>
                <Select value={msgChannelFilter} onValueChange={v => { setMsgChannelFilter(v); setMsgPage(0); }}>
                  <SelectTrigger className="w-[130px]" data-testid="msg-channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={msgStatusFilter} onValueChange={v => { setMsgStatusFilter(v); setMsgPage(0); }}>
                  <SelectTrigger className="w-[130px]" data-testid="msg-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="read">Read</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={msgDirectionFilter} onValueChange={v => { setMsgDirectionFilter(v); setMsgPage(0); }}>
                  <SelectTrigger className="w-[130px]" data-testid="msg-direction-filter"><SelectValue placeholder="Direction" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No messages found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="py-2 px-3">Date</th>
                          <th className="py-2 px-3">Direction</th>
                          <th className="py-2 px-3">Channel</th>
                          <th className="py-2 px-3">To/From</th>
                          <th className="py-2 px-3">Customer</th>
                          <th className="py-2 px-3">Event</th>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3">Content</th>
                          <th className="py-2 px-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {messages.map(msg => {
                          const sBadge = STATUS_BADGES[msg.status] || { label: msg.status, class: 'bg-slate-100 text-slate-700' };
                          const cBadge = CHANNEL_BADGES[msg.channel] || { label: msg.channel, class: 'bg-slate-100 text-slate-700' };
                          return (
                            <tr key={msg.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`msg-row-${msg.id}`}>
                              <td className="py-2.5 px-3 whitespace-nowrap text-xs text-slate-500">{formatDate(msg.created_at)}</td>
                              <td className="py-2.5 px-3">
                                <Badge variant="outline" className="text-xs">{msg.direction === 'outbound' ? 'Out' : 'In'}</Badge>
                              </td>
                              <td className="py-2.5 px-3"><Badge className={`text-xs ${cBadge.class}`}>{cBadge.label}</Badge></td>
                              <td className="py-2.5 px-3 text-xs">{msg.direction === 'outbound' ? msg.to_number : msg.from_number}</td>
                              <td className="py-2.5 px-3 font-medium text-xs">{msg.customer_name || '-'}</td>
                              <td className="py-2.5 px-3 text-xs">{EVENT_LABELS[msg.event] || msg.event}</td>
                              <td className="py-2.5 px-3"><Badge className={`text-xs ${sBadge.class}`}>{sBadge.label}</Badge></td>
                              <td className="py-2.5 px-3 text-xs max-w-[200px] truncate">{msg.content}</td>
                              <td className="py-2.5 px-3">
                                <Button variant="ghost" size="sm" onClick={() => setPreviewDialog({ open: true, message: msg })} data-testid={`msg-view-${msg.id}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-xs text-slate-500">{msgTotal} messages total</p>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" disabled={msgPage === 0} onClick={() => setMsgPage(p => p - 1)} data-testid="msg-prev-page">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="px-3 py-1.5 text-sm">{msgPage + 1} / {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={msgPage >= totalPages - 1} onClick={() => setMsgPage(p => p + 1)} data-testid="msg-next-page">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======== TEMPLATES ======== */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Message Templates</CardTitle>
                  <CardDescription>Create and manage templates for automated and manual messages</CardDescription>
                </div>
                <Button size="sm" onClick={() => {
                  setTemplateForm({ event: '', name: '', sms_template: '', whatsapp_template: '', channels: ['sms', 'whatsapp'], is_active: true });
                  setTemplateDialog({ open: true, template: null });
                }} data-testid="create-template-btn"><Plus className="w-4 h-4 mr-1" />New Template</Button>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No templates yet</p>
              ) : (
                <div className="space-y-3">
                  {templates.map(tmpl => (
                    <div key={tmpl.id} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors" data-testid={`template-${tmpl.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-sm">{tmpl.name}</h4>
                          <Badge variant="outline" className="text-xs">{EVENT_LABELS[tmpl.event] || tmpl.event}</Badge>
                          {tmpl.channels?.map(ch => (
                            <Badge key={ch} className={`text-xs ${CHANNEL_BADGES[ch]?.class || 'bg-slate-100'}`}>{CHANNEL_BADGES[ch]?.label || ch}</Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleToggleTemplate(tmpl)} data-testid={`template-toggle-${tmpl.id}`}>
                            {tmpl.is_active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => {
                            setTemplateForm({
                              event: tmpl.event, name: tmpl.name,
                              sms_template: tmpl.sms_template || '', whatsapp_template: tmpl.whatsapp_template || '',
                              channels: tmpl.channels || ['sms', 'whatsapp'], is_active: tmpl.is_active
                            });
                            setTemplateDialog({ open: true, template: tmpl });
                          }} data-testid={`template-edit-${tmpl.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTemplate(tmpl.id)} data-testid={`template-delete-${tmpl.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3 text-xs text-slate-600">
                        {tmpl.sms_template && <div><span className="text-slate-400 block mb-1">SMS:</span><p className="truncate">{tmpl.sms_template}</p></div>}
                        {tmpl.whatsapp_template && <div><span className="text-slate-400 block mb-1">WhatsApp:</span><p className="truncate">{tmpl.whatsapp_template}</p></div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======== AUTOMATION RULES ======== */}
        <TabsContent value="automation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Automation Rules</CardTitle>
              <CardDescription>Control which events trigger automated messages and via which channels</CardDescription>
            </CardHeader>
            <CardContent>
              {config?.automation_rules ? (
                <div className="space-y-3">
                  {Object.entries(config.automation_rules).map(([event, rule]) => (
                    <div key={event} className="border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`rule-${event}`}>
                      <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleRule(event, 'enabled', !rule.enabled)} data-testid={`rule-toggle-${event}`}>
                          {rule.enabled ? <ToggleRight className="w-6 h-6 text-emerald-600" /> : <ToggleLeft className="w-6 h-6 text-slate-400" />}
                        </Button>
                        <div>
                          <p className="font-semibold text-sm">{EVENT_LABELS[event] || event}</p>
                          <p className="text-xs text-slate-500">Triggers when {event.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {['sms', 'whatsapp'].map(ch => {
                          const active = (rule.channels || []).includes(ch);
                          return (
                            <Button
                              key={ch}
                              variant={active ? 'default' : 'outline'}
                              size="sm"
                              className={active ? (ch === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-600 hover:bg-cyan-700') : ''}
                              onClick={() => {
                                const newCh = active ? (rule.channels || []).filter(c => c !== ch) : [...(rule.channels || []), ch];
                                handleToggleRule(event, 'channels', newCh);
                              }}
                              data-testid={`rule-channel-${event}-${ch}`}
                            >
                              {ch === 'whatsapp' ? <Smartphone className="w-3.5 h-3.5 mr-1" /> : <Phone className="w-3.5 h-3.5 mr-1" />}
                              {ch === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-400">Loading rules...</p>
              )}
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                WhatsApp-first with SMS fallback: When both channels are enabled, WhatsApp is attempted first. If it fails, SMS is used as fallback.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======== PROVIDER CONFIG ======== */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Provider Configuration</CardTitle>
              <CardDescription>Configure the messaging provider. Currently using <Badge className="bg-amber-100 text-amber-700 ml-1">{config?.provider || 'mock'}</Badge> provider</CardDescription>
            </CardHeader>
            <CardContent>
              {config && (
                <div className="space-y-4 max-w-lg">
                  <div className="space-y-1.5">
                    <Label>Provider</Label>
                    <Select value={config.provider || 'mock'} onValueChange={v => handleUpdateConfig({ provider: v })} data-testid="provider-select">
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mock">Mock (Database only)</SelectItem>
                        <SelectItem value="clickatell">Clickatell</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Mock provider stores messages in the database without external delivery. Switch to Clickatell when ready.</p>
                  </div>
                  {config.provider === 'clickatell' && (
                    <>
                      <div className="space-y-1.5">
                        <Label>API Key</Label>
                        <Input type="password" placeholder="Enter Clickatell API key" onChange={e => handleUpdateConfig({ api_key: e.target.value })} data-testid="provider-api-key" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>WhatsApp Number</Label>
                        <Input placeholder="+27..." value={config.whatsapp_number || ''} onChange={e => handleUpdateConfig({ whatsapp_number: e.target.value })} data-testid="provider-wa-number" />
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5">
                    <Label>SMS Sender ID</Label>
                    <Input value={config.sms_sender || ''} onChange={e => handleUpdateConfig({ sms_sender: e.target.value })} placeholder="2k2day" data-testid="provider-sms-sender" />
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <Button variant={config.fallback_enabled ? 'default' : 'outline'} size="sm" onClick={() => handleUpdateConfig({ fallback_enabled: !config.fallback_enabled })} data-testid="fallback-toggle">
                      {config.fallback_enabled ? <ToggleRight className="w-5 h-5 mr-1" /> : <ToggleLeft className="w-5 h-5 mr-1" />}
                      WhatsApp-to-SMS Fallback
                    </Button>
                    <span className="text-xs text-slate-500">{config.fallback_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Usage */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Monthly Channel Usage</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthly && (
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(stats.monthly).map(([channel, data]) => (
                    <div key={channel} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {channel === 'whatsapp' ? <Smartphone className="w-4 h-4 text-green-600" /> : <Phone className="w-4 h-4 text-cyan-600" />}
                        <h4 className="font-semibold text-sm">{channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}</h4>
                        <span className="ml-auto text-lg font-bold tabular-nums">{data.total}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-blue-50 rounded">
                          <p className="font-semibold tabular-nums">{data.sent || 0}</p><p className="text-slate-500">Sent</p>
                        </div>
                        <div className="text-center p-2 bg-emerald-50 rounded">
                          <p className="font-semibold tabular-nums">{data.delivered || 0}</p><p className="text-slate-500">Delivered</p>
                        </div>
                        <div className="text-center p-2 bg-red-50 rounded">
                          <p className="font-semibold tabular-nums">{data.failed || 0}</p><p className="text-slate-500">Failed</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ======== SEND MESSAGE DIALOG ======== */}
      <Dialog open={sendDialog.open} onOpenChange={o => setSendDialog({ open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Send Message</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <Select value={sendForm.customer_id} onValueChange={v => setSendForm(f => ({ ...f, customer_id: v }))} data-testid="send-customer-select">
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.phone || c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select value={sendForm.channel} onValueChange={v => setSendForm(f => ({ ...f, channel: v }))} data-testid="send-channel-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea rows={4} value={sendForm.content} onChange={e => setSendForm(f => ({ ...f, content: e.target.value }))} placeholder="Type your message..." data-testid="send-content" />
              {variables.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  <span className="text-xs text-slate-400 mr-1">Variables:</span>
                  {variables.map(v => (
                    <button key={v.key} type="button" className="text-xs bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded transition-colors" onClick={() => setSendForm(f => ({ ...f, content: f.content + `{${v.key}}` }))}>
                      {`{${v.key}}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Mock provider: message will be recorded in database but not delivered externally.
            </div>
            <Button className="w-full" onClick={handleSendMessage} disabled={loading} data-testid="send-confirm-btn">
              <Send className="w-4 h-4 mr-1" />{loading ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======== TEMPLATE EDIT DIALOG ======== */}
      <Dialog open={templateDialog.open} onOpenChange={o => setTemplateDialog({ open: o, template: o ? templateDialog.template : null })}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{templateDialog.template ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Event Trigger</Label>
                <Select value={templateForm.event} onValueChange={v => setTemplateForm(f => ({ ...f, event: v }))} data-testid="template-event-select">
                  <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="application_received">Application Received</SelectItem>
                    <SelectItem value="loan_approved">Loan Approved</SelectItem>
                    <SelectItem value="loan_declined">Loan Declined</SelectItem>
                    <SelectItem value="payment_reminder">Payment Reminder</SelectItem>
                    <SelectItem value="payment_overdue">Payment Overdue</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Template Name</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Loan Approved" data-testid="template-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>SMS Template</Label>
              <Textarea rows={3} value={templateForm.sms_template} onChange={e => setTemplateForm(f => ({ ...f, sms_template: e.target.value }))} placeholder="SMS message text..." data-testid="template-sms-input" />
            </div>
            <div className="space-y-1.5">
              <Label>WhatsApp Template</Label>
              <Textarea rows={4} value={templateForm.whatsapp_template} onChange={e => setTemplateForm(f => ({ ...f, whatsapp_template: e.target.value }))} placeholder="WhatsApp message text..." data-testid="template-wa-input" />
            </div>
            {variables.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-slate-400 mr-1">Available variables:</span>
                {variables.map(v => (
                  <span key={v.key} className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{`{${v.key}}`} = {v.sample}</span>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <Label className="text-sm pt-1">Channels:</Label>
              {['sms', 'whatsapp'].map(ch => {
                const active = (templateForm.channels || []).includes(ch);
                return (
                  <Button key={ch} variant={active ? 'default' : 'outline'} size="sm"
                    className={active ? (ch === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-600 hover:bg-cyan-700') : ''}
                    onClick={() => {
                      const newCh = active ? templateForm.channels.filter(c => c !== ch) : [...templateForm.channels, ch];
                      setTemplateForm(f => ({ ...f, channels: newCh }));
                    }}
                    data-testid={`template-channel-${ch}`}
                  >
                    {ch === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                  </Button>
                );
              })}
            </div>
            <Button className="w-full" onClick={handleSaveTemplate} disabled={loading} data-testid="template-save-btn">
              {loading ? 'Saving...' : (templateDialog.template ? 'Update Template' : 'Create Template')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======== MESSAGE PREVIEW DIALOG ======== */}
      <Dialog open={previewDialog.open} onOpenChange={o => setPreviewDialog({ open: o, message: o ? previewDialog.message : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Message Details</DialogTitle></DialogHeader>
          {previewDialog.message && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500 text-xs block">Direction</span><Badge variant="outline">{previewDialog.message.direction}</Badge></div>
                <div><span className="text-slate-500 text-xs block">Channel</span><Badge className={CHANNEL_BADGES[previewDialog.message.channel]?.class || ''}>{CHANNEL_BADGES[previewDialog.message.channel]?.label || previewDialog.message.channel}</Badge></div>
                <div><span className="text-slate-500 text-xs block">Status</span><Badge className={STATUS_BADGES[previewDialog.message.status]?.class || ''}>{STATUS_BADGES[previewDialog.message.status]?.label || previewDialog.message.status}</Badge></div>
                <div><span className="text-slate-500 text-xs block">Event</span><p className="font-medium">{EVENT_LABELS[previewDialog.message.event] || previewDialog.message.event}</p></div>
                <div><span className="text-slate-500 text-xs block">Customer</span><p className="font-medium">{previewDialog.message.customer_name || '-'}</p></div>
                <div><span className="text-slate-500 text-xs block">Phone</span><p className="font-medium">{previewDialog.message.to_number || previewDialog.message.from_number}</p></div>
                <div><span className="text-slate-500 text-xs block">Reference</span><p className="font-medium">{previewDialog.message.reference || '-'}</p></div>
                <div><span className="text-slate-500 text-xs block">Date</span><p className="font-medium">{formatDate(previewDialog.message.created_at)}</p></div>
                {previewDialog.message.sent_by && <div><span className="text-slate-500 text-xs block">Sent By</span><p className="font-medium">{previewDialog.message.sent_by}</p></div>}
                {previewDialog.message.provider_message_id && <div><span className="text-slate-500 text-xs block">Provider ID</span><p className="font-medium text-xs">{previewDialog.message.provider_message_id}</p></div>}
              </div>
              <div className="border rounded-lg p-4 bg-slate-50">
                <span className="text-slate-400 text-xs block mb-1">Content</span>
                <p className="text-sm whitespace-pre-wrap">{previewDialog.message.content}</p>
              </div>
              {previewDialog.message.error && (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                  <XCircle className="w-4 h-4 inline mr-1" />Error: {previewDialog.message.error}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Per-application messaging panel, to be used inside application detail dialogs.
 */
export function ApplicationMessages({ applicationId, customerId: propCustomerId, getAuthHeader }) {
  const [messages, setMessages] = useState([]);
  const [sendContent, setSendContent] = useState('');
  const [sendChannel, setSendChannel] = useState('sms');
  const [loading, setLoading] = useState(false);

  const fetchAppMessages = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await axios.get(`${API_URL}/admin/applications/${applicationId}/messages`, getAuthHeader());
      setMessages(res.data || []);
    } catch (e) { /* ignore */ }
  }, [applicationId, getAuthHeader]);

  useEffect(() => { fetchAppMessages(); }, [fetchAppMessages]);

  const handleSend = async () => {
    if (!sendContent.trim() || !propCustomerId) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/messaging/send`, {
        customer_id: propCustomerId, application_id: applicationId, channel: sendChannel, content: sendContent
      }, getAuthHeader());
      toast.success('Message sent');
      setSendContent('');
      fetchAppMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="app-messages-panel">
      <h4 className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" />Communications ({messages.length})</h4>
      {messages.length > 0 ? (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {messages.map(msg => (
            <div key={msg.id} className={`p-2.5 rounded-lg text-xs ${msg.direction === 'inbound' ? 'bg-blue-50 ml-0 mr-8' : 'bg-slate-50 ml-8 mr-0'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`text-[10px] ${CHANNEL_BADGES[msg.channel]?.class || ''}`}>{msg.channel?.toUpperCase()}</Badge>
                <Badge className={`text-[10px] ${STATUS_BADGES[msg.status]?.class || ''}`}>{msg.status}</Badge>
                <span className="ml-auto text-slate-400">{new Date(msg.created_at).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 py-2">No messages for this application</p>
      )}
      <div className="flex gap-2">
        <Select value={sendChannel} onValueChange={setSendChannel}>
          <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sms">SMS</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Input className="h-8 text-xs flex-1" value={sendContent} onChange={e => setSendContent(e.target.value)} placeholder="Type message..." data-testid="app-msg-input" />
        <Button size="sm" className="h-8" onClick={handleSend} disabled={loading || !sendContent.trim() || !propCustomerId} data-testid="app-msg-send">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
